/**
 * Delete File/Folder Action
 * Deletes a file or folder (and all its contents)
 */

import { Datum } from "../datastructure";
import {
  ActionDefinition,
  ActionContext,
  getFilePayload,
  toApiPath,
} from "./types";
import deleteUserUploadedFile from "@/components/FpAppV4/services/deleteUserUploadedFile";
import { isReadOnlyDatum } from "../specialFolders";

/**
 * Recursively collects all file IDs from a datum (file or folder)
 */
export function collectFileIds(datum: Datum): string[] {
  const fileIds: string[] = [];

  if (datum.type === "file" && datum.payload?.type === "file") {
    fileIds.push(datum.payload.fileId);
  }

  if (datum.children) {
    for (const child of datum.children) {
      fileIds.push(...collectFileIds(child));
    }
  }

  return fileIds;
}

/**
 * Deletes a file
 */
export async function deleteFile(
  datum: Datum,
  context: ActionContext
): Promise<void> {
  const payload = getFilePayload(datum);
  if (!payload) {
    console.error("deleteFile: Cannot delete non-file datum");
    return;
  }

  try {
    await deleteUserUploadedFile([payload.fileId]);
    if (context.filetree) {
      context.filetree.removeDatums(new Set([datum.id]));
    } else {
      context.refreshTree();
    }
  } catch (error) {
    console.error("deleteFile: Failed to delete file", error);
    // Toast is shown by deleteUserUploadedFile
  }
}

/**
 * Deletes a folder and all its contents
 */
export async function deleteFolder(
  datum: Datum,
  context: ActionContext
): Promise<void> {
  if (datum.type !== "folder") {
    console.error("deleteFolder: Cannot delete non-folder datum");
    return;
  }

  const fileIds = collectFileIds(datum);
  const apiPath = toApiPath(datum.path, context.filetree);
  const driveId = datum.payload?.driveId ?? null;

  try {
    await deleteUserUploadedFile(fileIds, [{ path: apiPath, driveId }]);
    if (context.filetree) {
      const datumIds = new Set<string>();
      datumIds.add(datum.id);
      const addDescendants = (d: Datum) => {
        for (const child of d.children ?? []) {
          datumIds.add(child.id);
          addDescendants(child);
        }
      };
      addDescendants(datum);
      context.filetree.removeDatums(datumIds);
    } else {
      context.refreshTree();
    }
  } catch (error) {
    console.error("deleteFolder: Failed to delete folder", error);
    // Toast is shown by deleteUserUploadedFile
  }
}

/**
 * Action definition for context menu (files)
 */
export const deleteFileAction: ActionDefinition = {
  config: {
    action: "delete",
    label: "Delete",
  },
  isApplicable: (datum, context) =>
    datum.type === "file" && !isReadOnlyDatum(datum, context),
  execute: deleteFile,
};

/**
 * Action definition for context menu (folders)
 */
export const deleteFolderAction: ActionDefinition = {
  config: {
    action: "delete",
    label: "Delete",
  },
  isApplicable: (datum, context) =>
    datum.type === "folder" &&
    datum.path.length > 0 &&
    !isReadOnlyDatum(datum, context),
  execute: deleteFolder,
};
